import React, { useState, useRef } from "react";
import Tesseract from "tesseract.js";
import {
  Upload,
  ScanLine,
  Server,
  CheckCircle2,
  ArrowRight,
  RefreshCcw,
  FileText,
  AlertCircle,
  HelpCircle,
  Loader2,
  Sparkles,
  Check,
  RefreshCw,
  Smartphone,
  Mail,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { API_BASE_URL } from "../../config/baseUrls";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const TicketResolution = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [status, setStatus] = useState("IDLE");
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setPreview(URL.createObjectURL(selected));
      setStatus("IDLE");
      setResult(null);
      setProgress(0);
    }
  };

  const startResolution = async () => {
    if (!file) return;
    setStatus("SCANNING");

    try {
      const {
        data: { text },
      } = await Tesseract.recognize(file, "eng", {
        logger: (m) => {
          if (m.status === "recognizing text") {
            setProgress(Math.round(m.progress * 100));
          }
        },
      });

      const match = text.match(/(pay_[a-zA-Z0-9]+)/);

      if (match && match[0]) {
        verifyPayment(match[0]);
      } else {
        throw new Error(
          "Could not detect a valid Payment ID (pay_...). Please ensure the screenshot is clear."
        );
      }
    } catch (error) {
      handleError(error.message || "Failed to read image.");
    }
  };

  const verifyPayment = async (paymentId) => {
    setStatus("VERIFYING");

    await new Promise((r) => setTimeout(r, 800));

    try {
      const response = await fetch(
        `${API_BASE_URL}/tickets/resolve-ticket`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentId }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setStatus("SUCCESS");
      } else {
        throw new Error(data.message || "Verification failed.");
      }
    } catch (error) {
      handleError(error.message);
    }
  };

  const handleError = (msg) => {
    setResult({ success: false, message: msg });
    setStatus("ERROR");
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setStatus("IDLE");
    setResult(null);
    setProgress(0);
  };

  // Helper to clean up status text for mobile
  const getDisplayStatus = (status) => {
    if (status === "UPDATED TO CONFIRMED") return "CONFIRMED";
    return status;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans text-slate-900">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl shadow-blue-900/10 overflow-hidden border border-slate-100 relative">
        {/* APP HEADER */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        <div className="pt-8 pb-6 px-8 text-center space-y-1">
          <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-50 rounded-xl mb-3 text-blue-600">
            <Sparkles className="w-5 h-5" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">
            Payment Help
          </h1>
          <p className="text-slate-500 text-sm font-medium">
            Automated Ticket Recovery
          </p>
        </div>

        {/* PROGRESS STEPPER */}
        <div className="bg-slate-50/50 border-y border-slate-100 px-6 py-4">
          <div className="flex justify-between items-center relative z-0">
            <div className="absolute top-1/3 left-0 right-0 h-0.5 bg-slate-200 -z-10 transform -translate-y-1/2 mx-4" />
            <StepIndicator
              current={status}
              step="IDLE"
              icon={Upload}
              label="Upload"
              index={0}
            />
            <StepIndicator
              current={status}
              step="SCANNING"
              icon={ScanLine}
              label="Scan"
              index={1}
            />
            <StepIndicator
              current={status}
              step="VERIFYING"
              icon={Server}
              label="Verify"
              index={2}
            />
            <StepIndicator
              current={status}
              step="SUCCESS"
              icon={CheckCircle2}
              label="Done"
              index={3}
            />
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="p-6 min-h-[400px] flex flex-col justify-center">
          <AnimatePresence mode="wait">
            {/* 1. UPLOAD */}
            {(status === "IDLE" || status === "ERROR") && !result?.success && (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="space-y-6"
              >
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                  <div className="bg-blue-100 p-1.5 rounded-lg h-fit">
                    <HelpCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-sm text-blue-800">
                    <p className="font-bold mb-1">What to upload?</p>
                    <p className="text-blue-600/80 leading-relaxed">
                      Please upload the Razorpay receipt showing the{" "}
                      <strong className="font-mono text-blue-700 bg-blue-100/50 px-1 rounded">
                        Payment ID
                      </strong>{" "}
                      starting with <span className="font-mono">pay_...</span>{" "}
                      <span>
                        (Received on your email or payment confirmation page).
                      </span>
                    </p>
                  </div>
                </div>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "relative group cursor-pointer flex flex-col items-center justify-center w-full aspect-[4/3] rounded-2xl border-2 border-dashed transition-all duration-300",
                    preview
                      ? "border-blue-500 bg-blue-50/20"
                      : "border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-blue-400"
                  )}
                >
                  {preview ? (
                    <div className="relative w-full h-full p-2">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-full object-contain rounded-xl"
                      />
                      <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl backdrop-blur-sm">
                        <p className="text-white font-semibold flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full border border-white/30">
                          <RefreshCcw className="w-4 h-4" /> Replace
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center space-y-4 p-6">
                      <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center mx-auto text-blue-600 group-hover:scale-110 transition-transform duration-300">
                        <Upload className="w-8 h-8" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-base font-bold text-slate-700">
                          Tap to Upload Screenshot
                        </p>
                        <p className="text-xs text-slate-400 font-medium">
                          Payment Receipt (JPG/PNG)
                        </p>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                </div>

                {status === "ERROR" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="bg-red-50 border border-red-100 rounded-xl p-4 flex gap-3 text-red-600"
                  >
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium">{result?.message}</p>
                  </motion.div>
                )}

                <button
                  onClick={startResolution}
                  disabled={!file}
                  className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-lg shadow-lg shadow-blue-500/25 transition-all flex items-center justify-center gap-2"
                >
                  Resolve Issue <ArrowRight className="w-5 h-5" />
                </button>
              </motion.div>
            )}

            {/* 2. SCANNING */}
            {status === "SCANNING" && (
              <ProcessingState
                key="scanning"
                icon={<ScanLine className="w-12 h-12 text-white" />}
                title="Scanning Receipt"
                subtitle="Reading Transaction ID..."
                color="bg-blue-500"
                progress={progress}
              />
            )}

            {/* 3. VERIFYING */}
            {status === "VERIFYING" && (
              <ProcessingState
                key="verifying"
                icon={<Server className="w-12 h-12 text-white" />}
                title="Verifying Payment"
                subtitle="Checking bank records..."
                color="bg-indigo-500"
                isIndeterminate={true}
              />
            )}

            {/* 4. SUCCESS */}
            {status === "SUCCESS" && result && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6 text-center"
              >
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>

                <div className="space-y-1">
                  <h2 className="text-2xl font-black text-slate-900">
                    {result.title || "Success!"}
                  </h2>
                  <p className="text-slate-500 text-sm leading-relaxed px-4">
                    {result.message}
                  </p>
                </div>

                {/* DIGITAL TICKET STUB */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm relative overflow-hidden text-left mx-2 mt-4 group transition-all hover:shadow-md">
                  <div className="h-2 bg-green-500 w-full" />

                  <div className="p-5 space-y-4">
                    {/* Fixed Layout: Flex row with items-center */}
                    <div className="flex justify-between items-center border-b border-dashed border-slate-200 pb-4">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Item Type
                        </p>
                        <p className="font-bold text-slate-800 text-lg leading-none">
                          {result.data?.type || "Ticket"}
                        </p>
                      </div>

                      {/* Fixed Badge: Whitespace nowrap + Flex Alignment */}
                      <div className="text-right">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Status
                        </p>
                        <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-green-100 text-green-700 border border-green-200">
                          <Check className="w-3 h-3 mr-1.5" />
                          <span className="text-xs font-bold whitespace-nowrap">
                            {getDisplayStatus(result.data?.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <div className="overflow-hidden">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          Linked Account
                        </p>
                        <p className="font-bold text-lg text-slate-800 truncate">
                          {result.data?.nickname}
                        </p>
                        <p className="font-mono text-xs text-slate-400 truncate mt-0.5">
                          email: {result.data?.email || "N/A"}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex-shrink-0 flex items-center justify-center text-slate-400 ml-3 border border-slate-200">
                        <span className="text-lg font-bold">
                          {result.data?.nickname?.charAt(0).toUpperCase() ||
                            "U"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-left mx-2">
                  <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-2">
                    <Smartphone className="w-4 h-4" />
                    Not seeing it in the app?
                  </h4>
                  <ul className="text-xs text-amber-800 space-y-1.5 ml-1">
                    <li className="flex items-start gap-2">
                      <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Confirm Linked Email</strong>
                      </span>
                    </li>

                    <li className="flex items-start gap-2">
                      <span className="font-bold border border-amber-300 rounded px-1 text-[10px] bg-white">
                        X
                      </span>
                      <span>
                        <strong>Restart the app</strong> completely to sync
                        data.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <RefreshCw className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Update the App</strong> to the latest version.
                      </span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={reset}
                  className="mt-4 text-slate-500 font-semibold text-sm hover:text-slate-800 transition-colors py-2"
                >
                  Analyze Another Screenshot
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
          <p className="text-[10px] text-slate-400 font-medium flex items-center justify-center gap-1">
            <span>🔒</span> Secured by 256-bit SSL Encryption
          </p>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const StepIndicator = ({ current, step, icon: Icon, label, index }) => {
  const steps = ["IDLE", "SCANNING", "VERIFYING", "SUCCESS", "ERROR"];
  const currentIdx = steps.indexOf(current === "ERROR" ? "IDLE" : current);
  const isActive = index === currentIdx;
  const isCompleted = index < currentIdx;

  return (
    <div className="flex flex-col items-center gap-1.5 relative z-10">
      <div
        className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 border-2",
          isActive
            ? "bg-blue-600 border-blue-600 text-white scale-110 shadow-lg shadow-blue-500/30"
            : isCompleted
            ? "bg-blue-600 border-blue-600 text-white"
            : "bg-white border-slate-200 text-slate-300"
        )}
      >
        <Icon className="w-4 h-4" />
      </div>
      <span
        className={cn(
          "text-[10px] font-bold tracking-wide transition-colors duration-300",
          isActive || isCompleted ? "text-blue-700" : "text-slate-300"
        )}
      >
        {label}
      </span>
    </div>
  );
};

const ProcessingState = ({
  icon,
  title,
  subtitle,
  color,
  progress,
  isIndeterminate,
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 1.1 }}
    className="flex flex-col items-center justify-center text-center py-8"
  >
    <div className="relative mb-8">
      <div
        className={cn(
          "absolute inset-0 rounded-full animate-ping opacity-20",
          color
        )}
      ></div>
      <div
        className={cn(
          "relative w-24 h-24 rounded-full shadow-xl flex items-center justify-center",
          color
        )}
      >
        {icon}
      </div>
      <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-full shadow-md">
        <Loader2
          className={cn("w-5 h-5 animate-spin", color.replace("bg-", "text-"))}
        />
      </div>
    </div>

    <h3 className="text-2xl font-bold text-slate-800 mb-2">{title}</h3>
    <p className="text-slate-500 font-medium mb-8">{subtitle}</p>

    <div className="w-full max-w-[200px] h-2 bg-slate-100 rounded-full overflow-hidden">
      {isIndeterminate ? (
        <div
          className={cn(
            "h-full w-full animate-indeterminate rounded-full",
            color
          )}
        />
      ) : (
        <div
          className={cn(
            "h-full transition-all duration-300 ease-out rounded-full",
            color
          )}
          style={{ width: `${progress}%` }}
        />
      )}
    </div>
  </motion.div>
);

export default TicketResolution;
